import React from "react";
import {
  Search, X, AlertTriangle, Inbox, Wrench, Workflow as SubIcon,
  Truck, DollarSign, RefreshCw, Star, Clock, BookmarkPlus, Bookmark,
  MoreVertical, MessageSquare, Eye, FileText, Undo2, ChevronDown, ChevronUp, BarChart3,
} from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { JobDetailModal } from "../components/JobDetailModal";
import { Modal } from "../components/Modal";
import {
  SubconDropModal, DispatchReadyModal, InvoiceModal, CustomerIssueModal,
} from "../components/JobFlowModals";
import { WorkloadByJobChart } from "../components/WorkloadByJobChart";
import { ThroughputChart } from "../components/ThroughputChart";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import "../styles/JobFlowBoard.css";

const COLUMNS = [
  { key: "receiving",        title: "Receiving",         icon: Inbox },
  { key: "technical",        title: "Technical",         icon: Wrench },
  { key: "admin",            title: "Admin / Updating",  icon: FileText },
  { key: "subcontractor",    title: "Subcontractor",     icon: SubIcon },
  { key: "dispatch",         title: "Dispatch",          icon: Truck },
  { key: "awaiting_payment", title: "Awaiting Payment",  icon: DollarSign },
];

// Order of sub-status sections within each column
const SUB_ORDER = {
  technical: ["Assigned", "In Progress", "Awaiting Spares", "Awaiting Customer", "Back from Parts", "Unserviceable Review", "Ready for Admin", "In Dispatch"],
  subcontractor: ["Sent to subcontractor", "At subcontractor", "Returned"],
  dispatch: ["In Dispatch", "Ready for Collection", "Collected", "Delivered", "Closed"],
  awaiting_payment: ["Not invoiced", "Invoice issued", "Awaiting payment"],
};

// On drop to column X, what is the target operational/payment change?
// Returns { target_status?, payment_status?, requires: [fields needed if missing] }
function dropPlan(card, targetCol) {
  const cur = card.status;
  if (targetCol === "receiving") return { error: "Cards cannot be dragged back to Receiving. Use the side panel to override." };
  if (targetCol === "technical") {
    if (["awaiting_subcontractor", "at_subcontractor", "returned_from_subcontractor"].includes(cur)) {
      return { target_status: "returned_from_subcontractor" };
    }
    if (cur === "received") {
      return { target_status: "assigned", requires: ["technician_id"] };
    }
    if (cur === "ready_for_admin" || ["dispatched", "delivered", "collected", "closed"].includes(cur)) {
      return { target_status: "in_progress", requires: card.technician_id ? [] : ["technician_id"], note: "Sent back to the lab" };
    }
    return { error: "Card is already in Technical." };
  }
  if (targetCol === "admin") {
    if (cur === "ready_for_admin") return { error: "Already in Admin / Updating." };
    if (["dispatched", "delivered", "collected", "closed"].includes(cur)) {
      return { target_status: "ready_for_admin", note: "Re-opened from dispatch" };
    }
    return { target_status: "ready_for_admin", requires: card.calibration_result ? [] : ["calibration_result"] };
  }
  if (targetCol === "subcontractor") {
    if (["at_subcontractor", "awaiting_subcontractor"].includes(cur)) return { error: "Already at subcontractor." };
    return { target_status: "at_subcontractor", requires: ["subcontractor_name"] };
  }
  if (targetCol === "dispatch") {
    if (["dispatched", "delivered", "collected", "closed"].includes(cur)) return { error: "Already in Dispatch." };
    return { target_status: "ready_for_delivery", requires: card.calibration_result ? [] : ["calibration_result"] };
  }
  if (targetCol === "awaiting_payment") {
    // Commercial-only change; operational state unchanged
    return { payment_status: "awaiting_payment", requires: card.invoice_number ? [] : ["invoice_number"] };
  }
  return { error: "Unknown target column" };
}

const REQ_LABELS = {
  technician_id: "A technician must be assigned",
  subcontractor_name: "Subcontractor name",
  calibration_result: "Technical / calibration result",
  invoice_number: "Invoice number",
  delivery_method: "Dispatch method",
  delivery_date: "Dispatch date",
};

function ageClass(days) {
  if (days == null) return "normal";
  if (days <= 7) return "normal";
  if (days <= 14) return "warn";
  return "over";
}

function Card({ c, onPointerDown, onClick, onContextMenu, dragging }) {
  const age = ageClass(c.days_in_system);
  const missing = (c.missing_info || []).length > 0;
  return (
    <div
      className={`jfb-card ${dragging ? "dragging" : ""}`}
      data-card-id={c.id}
      data-testid={`jfb-card-${c.id}`}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      onClick={(e) => { if (!e.defaultPrevented) onClick(c); }}
    >
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="dn">
          {c.customer_number && <span style={{ color: "#6b521a", marginRight: 4 }}>{c.customer_number} ·</span>}
          {c.delivery_note_number || "—"}
        </span>
        {c.priority === "urgent" && <Star size={13} className="pri-flag" fill="#c0303a" data-testid={`jfb-card-${c.id}-urgent`} />}
      </div>
      <div className="desc">{c.description || "Unnamed item"}</div>
      <div className="meta">
        <span>{c.customer_name || "—"}</span>
      </div>
      <div className="meta">
        {c.serial_number && <span className="mono">SN {c.serial_number}</span>}
        {c.issue_number && <span className="mono">IT {c.issue_number}</span>}
      </div>
      <div className="meta">
        <span>{c.discipline || "—"}</span>
        <span>·</span>
        <span>{c.technician_name || "Unassigned"}</span>
      </div>
      <div className="badges">
        <span className={`age ${age}`}><Clock size={10} style={{ verticalAlign: -1, marginRight: 3 }} />{c.days_in_system != null ? `${c.days_in_system}d in system` : "—"}</span>
        {c.hold_reason && <span className="hold">{c.hold_reason}</span>}
        {missing && <span className="missing" title="Missing serial / asset / discipline"><AlertTriangle size={10} style={{ verticalAlign: -1, marginRight: 3 }} />missing info</span>}
      </div>
      {c.is_unserviceable && (
        <div className="badges" style={{ marginTop: 4 }} data-testid={`jfb-card-${c.id}-usr`}>
          <span style={{
            background: "#fde2e2", color: "#a32424",
            fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6,
            display: "inline-flex", alignItems: "center", gap: 3,
          }}>
            <AlertTriangle size={10} /> USR
          </span>
          {c.unserviceable_reason_label && (
            <span style={{
              background: "#fff4d6", color: "#8a5a00",
              fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 6,
              textTransform: "capitalize",
            }}>{c.unserviceable_reason_label}</span>
          )}
          {c.unserviceable_days_in_review != null && (
            <span className={`age ${ageClass(c.unserviceable_days_in_review)}`}
              title="Days waiting for admin review">
              <Clock size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
              review {c.unserviceable_days_in_review}d
            </span>
          )}
          {c.customer_advised ? (
            <span style={{
              background: "#dff6e3", color: "#1d6a3a",
              fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 6,
            }} data-testid={`jfb-card-${c.id}-advised`}>✓ advised</span>
          ) : (
            <span style={{
              background: "#fde2e2", color: "#a32424",
              fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6,
              display: "inline-flex", alignItems: "center", gap: 3,
            }} data-testid={`jfb-card-${c.id}-not-advised`}>
              <AlertTriangle size={10} /> not advised
            </span>
          )}
        </div>
      )}
      {c.return_to_customer_requested && (
        <div className="badges" style={{ marginTop: 4 }} data-testid={`jfb-card-${c.id}-rtc`}>
          <span style={{
            background: "#e2ecfd", color: "#1e4b9a",
            fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6,
            display: "inline-flex", alignItems: "center", gap: 3,
          }} title={c.return_to_customer_reason || "Customer decided not to continue"}>
            <Undo2 size={10} /> RtC
          </span>
        </div>
      )}
    </div>
  );
}

function MissingInfoModal({ open, card, plan, onClose, onOpenItem }) {
  if (!open || !plan) return null;
  return (
    <Modal open onClose={onClose} icon={AlertTriangle} title="More info needed before this move"
      subtitle={`${card?.description || "Item"} · ${card?.delivery_note_number || ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} data-testid="jfb-mi-cancel">Cancel</button>
          <button className="btn gold" onClick={() => { onClose(); onOpenItem(); }} data-testid="jfb-mi-open">Open item to complete</button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, color: "var(--text-2)", margin: 0 }}>
        The following fields must be set before this card can move to its new column:
      </p>
      <ul className="jfb-req-list" data-testid="jfb-mi-req-list">
        {(plan.requires || []).map((k) => (
          <li key={k}><AlertTriangle size={14} color="#c0303a" />{REQ_LABELS[k] || k}</li>
        ))}
      </ul>
    </Modal>
  );
}

export default function JobFlowBoard() {
  const { can } = useAuth();
  const canEdit = can("job_flow", "edit");
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState({
    customer_id: "", technician_id: "", discipline: "", priority: "all",
    urgentOnly: false, awaiting_customer: false, awaiting_spares: false,
    subcontractor: false, payment_outstanding: false, include_closed: false,
  });
  const [openJobId, setOpenJobId] = React.useState(null);
  const [drag, setDrag] = React.useState(null); // {cardId, x, y, ghost}
  const [hoverCol, setHoverCol] = React.useState(null);
  const [missing, setMissing] = React.useState(null); // { card, plan }
  const [specialModal, setSpecialModal] = React.useState(null); // {kind, card}
  const [ctxMenu, setCtxMenu] = React.useState(null); // {card, x, y}
  const [savedViews, setSavedViews] = React.useState([]);
  const [activeViewId, setActiveViewId] = React.useState(null);
  const [savingView, setSavingView] = React.useState(false);
  // Default: group items by Delivery-Note number so the board shows Jobs as
  // the primary unit of work (multiple equipment per Job). Toggleable in case
  // the operator wants to see every line item individually.
  const [groupByJob, setGroupByJob] = React.useState(() => {
    const saved = localStorage.getItem("jfb.groupByJob");
    return saved === null ? true : saved === "true";
  });
  // SPT-PTE-2026-0046 — collapsible "Workload & Throughput" dashboard panel.
  // Collapsed by default so it never eats into the kanban's screen space
  // unless the operator opts in; remembered per-browser like groupByJob.
  const [dashOpen, setDashOpen] = React.useState(() => localStorage.getItem("jfb.dashboardOpen") === "true");
  const [expandedGroups, setExpandedGroups] = React.useState({}); // { "col:dn": true }
  const ghostRef = React.useRef(null);
  const longPressTimer = React.useRef(null);

  React.useEffect(() => {
    localStorage.setItem("jfb.groupByJob", String(groupByJob));
  }, [groupByJob]);

  React.useEffect(() => {
    localStorage.setItem("jfb.dashboardOpen", String(dashOpen));
  }, [dashOpen]);

  const load = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.technician_id) params.technician_id = filters.technician_id;
      if (filters.discipline) params.discipline = filters.discipline;
      if (filters.priority && filters.priority !== "all") params.priority = filters.priority;
      if (filters.include_closed) { params.include_closed = true; params.closed_within_days = 14; }
      const { data } = await api.get("/job-flow/board", { params });
      setData(data);
    } catch (e) {
      setErr(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => { load(); }, [load]);

  // Load saved views once
  React.useEffect(() => {
    api.get("/job-flow/views").then((r) => setSavedViews(r.data || [])).catch(() => {});
  }, []);

  // Close context menu on any outside interaction
  React.useEffect(() => {
    if (!ctxMenu) return undefined;
    const close = () => setCtxMenu(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("blur", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("blur", close);
    };
  }, [ctxMenu]);

  // Distinct technicians + disciplines on the current board for the dropdowns
  const techOpts = React.useMemo(() => {
    if (!data?.cards) return [];
    const seen = new Map();
    data.cards.forEach((c) => {
      if (c.technician_id && !seen.has(c.technician_id)) {
        seen.set(c.technician_id, c.technician_name || c.technician_id);
      }
    });
    return Array.from(seen, ([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);
  const discOpts = React.useMemo(() => {
    if (!data?.cards) return [];
    const s = new Set();
    data.cards.forEach((c) => { if (c.discipline) s.add(c.discipline); });
    return Array.from(s).sort();
  }, [data]);

  // Filtered + searched cards
  const cards = React.useMemo(() => {
    if (!data?.cards) return [];
    const q = search.trim().toLowerCase();
    return data.cards.filter((c) => {
      if (c.column === "closed" && !filters.include_closed) return false;
      if (filters.urgentOnly && c.priority !== "urgent") return false;
      if (filters.awaiting_customer && !["awaiting_customer_approval", "awaiting_quote"].includes(c.status)) return false;
      if (filters.awaiting_spares && !["parts_required", "awaiting_spares"].includes(c.status)) return false;
      if (filters.subcontractor && c.column !== "subcontractor") return false;
      if (filters.payment_outstanding && !["invoiced", "awaiting_payment"].includes(c.payment_status)) return false;
      if (filters.technician_id && c.technician_id !== filters.technician_id) return false;
      if (filters.discipline && (c.discipline || "") !== filters.discipline) return false;
      if (q) {
        const blob = [c.delivery_note_number, c.customer_name, c.description, c.serial_number, c.issue_number, c.certificate_number, c.technician_name]
          .filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, filters]);

  const byCol = React.useMemo(() => {
    const out = {};
    for (const col of COLUMNS) out[col.key] = [];
    for (const c of cards) {
      if (out[c.column]) out[c.column].push(c);
    }
    return out;
  }, [cards]);

  // Pointer drag — works on mouse and touch. Long-press (550ms hold without
  // movement) opens the quick-actions context menu on touch devices.
  const handleCardPointerDown = (card) => (e) => {
    if (!canEdit) return;
    if (e.button && e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const offX = startX - rect.left, offY = startY - rect.top;
    let moved = false;
    let longPressed = false;
    target.setPointerCapture?.(e.pointerId);

    // Long-press for context menu on touch
    if (e.pointerType === "touch") {
      longPressTimer.current = setTimeout(() => {
        longPressed = true;
        openCtxMenu(card, startX, startY);
        if (navigator.vibrate) navigator.vibrate(35);
      }, 550);
    }

    const clearLongPress = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    const onMove = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < 5) return;
      moved = true;
      clearLongPress();
      if (longPressed) return;
      if (!ghostRef.current) {
        const clone = target.cloneNode(true);
        clone.classList.add("jfb-ghost");
        clone.style.width = `${rect.width}px`;
        document.body.appendChild(clone);
        ghostRef.current = clone;
        setDrag({ cardId: card.id });
      }
      ghostRef.current.style.left = `${ev.clientX - offX}px`;
      ghostRef.current.style.top = `${ev.clientY - offY}px`;
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const col = el?.closest("[data-jfb-col]")?.getAttribute("data-jfb-col");
      setHoverCol(col || null);
    };

    const onUp = (ev) => {
      clearLongPress();
      target.releasePointerCapture?.(ev.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      const wasMoved = moved;
      const lastCol = (() => {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        return el?.closest("[data-jfb-col]")?.getAttribute("data-jfb-col");
      })();
      if (ghostRef.current) {
        ghostRef.current.remove();
        ghostRef.current = null;
      }
      setDrag(null);
      setHoverCol(null);
      if (longPressed) {
        // Prevent the click from firing after long-press
        ev.preventDefault?.();
        return;
      }
      if (wasMoved && lastCol && lastCol !== card.column) {
        attemptDrop(card, lastCol);
      }
    };

    const onCancel = () => {
      clearLongPress();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      setDrag(null); setHoverCol(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  const attemptDrop = async (card, targetCol) => {
    const plan = dropPlan(card, targetCol);
    if (plan.error) { toast.error(plan.error); return; }
    // Route to specialised modal when one is appropriate
    if (targetCol === "subcontractor") {
      setSpecialModal({ kind: "subcon", card });
      return;
    }
    if (targetCol === "dispatch" && plan.target_status === "ready_for_delivery") {
      setSpecialModal({ kind: "dispatch_ready", card });
      return;
    }
    if (targetCol === "awaiting_payment") {
      setSpecialModal({ kind: "invoice", card });
      return;
    }
    // Otherwise fall back to generic flow with optional missing-info modal
    if ((plan.requires || []).length > 0) {
      setMissing({ card, plan, targetCol });
      return;
    }
    try {
      if (plan.target_status) {
        await api.patch(`/job-items/${card.id}/status`, { status: plan.target_status, notes: plan.note });
      }
      if (plan.payment_status) {
        await api.patch(`/job-items/${card.id}/payment`, { payment_status: plan.payment_status });
      }
      toast.success("Card moved");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const setChip = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. 'Urgent + Awaiting Spares'):");
    if (!name?.trim()) return;
    setSavingView(true);
    try {
      const { data } = await api.post("/job-flow/views", { name: name.trim(), filters, search });
      setSavedViews((s) => [data, ...s]);
      setActiveViewId(data.id);
      toast.success(`Saved view "${data.name}"`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingView(false); }
  };

  const applyView = (v) => {
    setFilters({
      customer_id: "", technician_id: "", discipline: "", priority: "all",
      urgentOnly: false, awaiting_customer: false, awaiting_spares: false,
      subcontractor: false, payment_outstanding: false, include_closed: false,
      ...(v.filters || {}),
    });
    setSearch(v.search || "");
    setActiveViewId(v.id);
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await api.delete(`/job-flow/views/${v.id}`);
      setSavedViews((s) => s.filter((x) => x.id !== v.id));
      if (activeViewId === v.id) setActiveViewId(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Quick-actions context menu — opens on right-click or long-press
  const openCtxMenu = (card, x, y) => {
    if (!canEdit) return;
    // Clamp to viewport
    const maxX = window.innerWidth - 220;
    const maxY = window.innerHeight - 260;
    setCtxMenu({ card, x: Math.min(x, maxX), y: Math.min(y, maxY) });
  };

  if (loading && !data) return <><PageHead crumb="Operations" title="Job Flow Board" sub="Live view of every active job-line." /><Loader /></>;
  if (err) return <><PageHead crumb="Operations" title="Job Flow Board" /><div className="card pad" style={{ color: "var(--red)" }}>{err}</div></>;

  const s = data?.summary || {};

  return (
    <>
      <PageHead crumb="Operations" title="Job Flow Board"
        sub="Live view of every active job-line — from receiving through dispatch and payment."
        actions={<button className="btn ghost sm" onClick={load} data-testid="jfb-refresh"><RefreshCw size={14} /> Refresh</button>}
      />

      {/* SPT-PTE-2026-0046 — collapsible workload/throughput dashboard panel */}
      <div className="card pad" style={{ marginBottom: 18 }} data-testid="jfb-dashboard-panel">
        <div
          className="sec-h"
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => setDashOpen((v) => !v)}
          data-testid="jfb-dashboard-toggle"
        >
          <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BarChart3 size={16} /> Workload &amp; Throughput
          </h3>
          {dashOpen ? <ChevronUp size={16} color="var(--text-3)" /> : <ChevronDown size={16} color="var(--text-3)" />}
        </div>
        {dashOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 14 }} data-testid="jfb-dashboard-body">
            <WorkloadByJobChart />
            <ThroughputChart />
          </div>
        )}
      </div>

      {/* Summary strip */}
      <div className="jfb-stats" data-testid="jfb-summary">
        <div className="jfb-stat"><div className="lbl">Total active</div><div className="num" data-testid="jfb-sum-total">{s.total_active || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Receiving</div><div className="num">{s.receiving || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Technical</div><div className="num">{s.technical || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Admin / Updating</div><div className="num" data-testid="jfb-sum-admin">{s.admin || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Awaiting spares</div><div className="num">{s.awaiting_spares || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Awaiting customer</div><div className="num">{s.awaiting_customer || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Subcontractor</div><div className="num">{s.subcontractor || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">In Dispatch</div><div className="num">{s.ready_for_dispatch || 0}</div></div>
        <div className="jfb-stat"><div className="lbl">Awaiting payment</div><div className="num">{s.awaiting_payment || 0}</div></div>
        <div className="jfb-stat overdue"><div className="lbl">Overdue (15d+)</div><div className="num" data-testid="jfb-sum-overdue">{s.overdue || 0}</div></div>
      </div>

      {/* Filters */}
      <div className="jfb-filters">
        <div className="search" style={{ flex: "0 1 320px" }}>
          <Search size={14} />
          <input
            placeholder="Search DN, customer, serial, asset, cert…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="jfb-search"
          />
        </div>
        <select className="jfb-select"
          value={filters.technician_id}
          onChange={(e) => setFilters((f) => ({ ...f, technician_id: e.target.value }))}
          data-testid="jfb-filter-technician"
          title="Filter by technician">
          <option value="">All technicians</option>
          {techOpts.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="jfb-select"
          value={filters.discipline}
          onChange={(e) => setFilters((f) => ({ ...f, discipline: e.target.value }))}
          data-testid="jfb-filter-discipline"
          title="Filter by discipline">
          <option value="">All disciplines</option>
          {discOpts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <span className={`jfb-chip ${filters.urgentOnly ? "active" : ""}`} onClick={() => setChip("urgentOnly")} data-testid="jfb-chip-urgent">Urgent only</span>
        <span className={`jfb-chip ${filters.awaiting_customer ? "active" : ""}`} onClick={() => setChip("awaiting_customer")} data-testid="jfb-chip-awaiting-customer">Awaiting customer</span>
        <span className={`jfb-chip ${filters.awaiting_spares ? "active" : ""}`} onClick={() => setChip("awaiting_spares")} data-testid="jfb-chip-awaiting-spares">Awaiting spares</span>
        <span className={`jfb-chip ${filters.subcontractor ? "active" : ""}`} onClick={() => setChip("subcontractor")} data-testid="jfb-chip-subcontractor">Subcontractor</span>
        <span className={`jfb-chip ${filters.payment_outstanding ? "active" : ""}`} onClick={() => setChip("payment_outstanding")} data-testid="jfb-chip-payment">Payment outstanding</span>
        <span className={`jfb-chip ${filters.include_closed ? "active" : ""}`} onClick={() => setChip("include_closed")} data-testid="jfb-chip-closed">Closed (last 14d)</span>
        <span className={`jfb-chip ${groupByJob ? "active" : ""}`} onClick={() => setGroupByJob((v) => !v)} data-testid="jfb-chip-group-by-job"
              title="Group items by Delivery Note number — one stack per Job">
          {groupByJob ? "Grouped by Job ✓" : "Group by Job"}
        </span>
        {(search || Object.values(filters).some((v) => v && v !== "all")) && (
          <span className="jfb-chip" onClick={() => { setSearch(""); setFilters({ customer_id: "", technician_id: "", discipline: "", priority: "all", urgentOnly: false, awaiting_customer: false, awaiting_spares: false, subcontractor: false, payment_outstanding: false, include_closed: false }); }} data-testid="jfb-chip-clear">
            <X size={11} /> Clear
          </span>
        )}
        {!canEdit && <span className="jfb-readonly">Read-only — your role can view but not move cards.</span>}
      </div>

      {/* Saved views bar */}
      {(savedViews.length > 0 || canEdit) && (
        <div className="jfb-filters" data-testid="jfb-views-bar" style={{ marginTop: -4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-3)" }}>Saved views</span>
          {savedViews.length === 0 && <span className="jfb-readonly">No saved views yet — save your current filters with the bookmark button.</span>}
          {savedViews.map((v) => (
            <span key={v.id} className={`jfb-chip ${activeViewId === v.id ? "active" : ""}`}
              data-testid={`jfb-view-${v.id}`}
              onClick={() => applyView(v)}>
              <Bookmark size={11} /> {v.name}
              <X size={11} style={{ marginLeft: 4, opacity: .6 }}
                onClick={(e) => { e.stopPropagation(); deleteView(v); }} />
            </span>
          ))}
          <button className="jfb-chip" onClick={saveView} disabled={savingView} data-testid="jfb-view-save">
            <BookmarkPlus size={11} /> Save current view
          </button>
        </div>
      )}

      {/* Columns */}
      <div className="jfb-cols" data-testid="jfb-cols">
        {COLUMNS.map(({ key, title, icon: Icon }) => {
          const items = byCol[key] || [];
          // Sub-grouping within columns
          const sections = (() => {
            const order = SUB_ORDER[key];
            if (!order) return [{ label: null, items }];
            const groups = {};
            for (const it of items) {
              const lab = it.sub_status_label || "Other";
              if (!groups[lab]) groups[lab] = [];
              groups[lab].push(it);
            }
            const ordered = order.filter((o) => groups[o]).map((o) => ({ label: o, items: groups[o] }));
            const leftover = Object.keys(groups).filter((k) => !order.includes(k)).map((k) => ({ label: k, items: groups[k] }));
            return [...ordered, ...leftover];
          })();
          return (
            <div
              key={key}
              className={`jfb-col ${hoverCol === key ? "over" : ""}`}
              data-jfb-col={key}
              data-testid={`jfb-col-${key}`}
            >
              <div className="jfb-col-head">
                <Icon size={15} />
                <span className="ttl">{title}</span>
                <span className="cnt" data-testid={`jfb-count-${key}`}>{items.length}</span>
              </div>
              <div className="jfb-col-body">
                {sections.map(({ label, items: secItems }) => {
                  // Optionally group items in this section by delivery_note_number
                  // so a Job with N items shows as ONE stack instead of N cards.
                  const groups = (() => {
                    if (!groupByJob) return [{ key: null, items: secItems }];
                    const map = new Map();
                    for (const it of secItems) {
                      const k = `${it.delivery_note_number || "—"}|${it.customer_id || ""}`;
                      if (!map.has(k)) map.set(k, []);
                      map.get(k).push(it);
                    }
                    return Array.from(map.entries()).map(([k, items]) => ({ key: k, items }));
                  })();
                  return (
                    <div key={label || "all"}>
                      {label && <div className="jfb-sub-label">{label}</div>}
                      {groups.map(({ key: gkey, items: groupItems }) => {
                        // Solo items: render as before
                        if (!groupByJob || groupItems.length === 1) {
                          return groupItems.map((c) => (
                            <Card
                              key={c.id}
                              c={c}
                              dragging={drag?.cardId === c.id}
                              onPointerDown={handleCardPointerDown(c)}
                              onContextMenu={(e) => { e.preventDefault(); openCtxMenu(c, e.clientX, e.clientY); }}
                              onClick={(card) => setOpenJobId(card.job_id)}
                            />
                          ));
                        }
                        // Multi-item group: render a Job stack
                        const head = groupItems[0];
                        const expandKey = `${key}:${gkey}`;
                        const expanded = expandedGroups[expandKey];
                        const techs = Array.from(new Set(groupItems.map((it) => it.technician_name).filter(Boolean)));
                        const maxAge = Math.max(...groupItems.map((it) => it.days_in_system || 0));
                        const ageCls = ageClass(maxAge);
                        const urgent = groupItems.some((it) => it.priority === "urgent");
                        return (
                          <div key={gkey} className={`jfb-job-stack ${expanded ? "expanded" : ""}`}
                                data-testid={`jfb-stack-${head.job_id}`}>
                            <div className="jfb-job-stack-head"
                                  onClick={() => setExpandedGroups((p) => ({ ...p, [expandKey]: !expanded }))}>
                              <div className="row" style={{ justifyContent: "space-between" }}>
                                <span className="dn">
                                  {head.customer_number && <span style={{ color: "#6b521a", marginRight: 4 }}>{head.customer_number} ·</span>}
                                  {head.delivery_note_number || "—"}
                                </span>
                                <span className="badges" style={{ gap: 4 }}>
                                  {urgent && <Star size={12} fill="#c0303a" color="#c0303a" />}
                                  <span className={`age ${ageCls}`} style={{ fontSize: 10 }}>
                                    <Clock size={9} style={{ verticalAlign: -1, marginRight: 2 }} />{maxAge}d
                                  </span>
                                  <span className="jfb-stack-count">{groupItems.length} items</span>
                                </span>
                              </div>
                              <div className="meta">
                                <span style={{ fontWeight: 600 }}>{head.customer_name || "—"}</span>
                              </div>
                              <div className="meta">
                                <span>{techs.length === 0 ? "Unassigned"
                                   : techs.length === 1 ? techs[0]
                                   : `${techs.length} techs`}</span>
                                <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--gold-deep)" }}>
                                  {expanded ? "▴ collapse" : "▾ expand"}
                                </span>
                              </div>
                            </div>
                            {expanded && (
                              <div className="jfb-job-stack-items">
                                {groupItems.map((c) => (
                                  <Card
                                    key={c.id}
                                    c={c}
                                    dragging={drag?.cardId === c.id}
                                    onPointerDown={handleCardPointerDown(c)}
                                    onContextMenu={(e) => { e.preventDefault(); openCtxMenu(c, e.clientX, e.clientY); }}
                                    onClick={(card) => setOpenJobId(card.job_id)}
                                  />
                                ))}
                              </div>
                            )}
                            {!expanded && (
                              // Show first 3 item descriptions as a peek
                              <div className="jfb-job-stack-peek">
                                {groupItems.slice(0, 3).map((c) => (
                                  <div key={c.id} className="jfb-stack-peek-row" onClick={() => setOpenJobId(c.job_id)}>
                                    <span className="mono" style={{ fontSize: 10.5, color: "var(--text-3)", marginRight: 4 }}>
                                      {c.serial_number || c.issue_number || "—"}
                                    </span>
                                    {c.description || "Item"}
                                  </div>
                                ))}
                                {groupItems.length > 3 && (
                                  <div className="jfb-stack-peek-more">+{groupItems.length - 3} more</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {!items.length && <div className="t-mut" style={{ fontSize: 12, padding: "12px 6px" }}>No items.</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Side panel — reuse JobDetailModal which shows all items in the job */}
      {openJobId && (
        <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)} onChanged={load} />
      )}

      <MissingInfoModal
        open={!!missing}
        card={missing?.card}
        plan={missing?.plan}
        onClose={() => setMissing(null)}
        onOpenItem={() => missing?.card?.job_id && setOpenJobId(missing.card.job_id)}
      />

      {/* Specialised v2 modals */}
      {specialModal?.kind === "subcon" && (
        <SubconDropModal card={specialModal.card}
          onClose={() => setSpecialModal(null)}
          onDone={() => { setSpecialModal(null); load(); }} />
      )}
      {specialModal?.kind === "dispatch_ready" && (
        <DispatchReadyModal card={specialModal.card}
          onClose={() => setSpecialModal(null)}
          onDone={() => { setSpecialModal(null); load(); }} />
      )}
      {specialModal?.kind === "invoice" && (
        <InvoiceModal card={specialModal.card}
          onClose={() => setSpecialModal(null)}
          onDone={() => { setSpecialModal(null); load(); }} />
      )}
      {specialModal?.kind === "customer_issue" && (
        <CustomerIssueModal card={specialModal.card}
          onClose={() => setSpecialModal(null)}
          onDone={() => { setSpecialModal(null); load(); }} />
      )}

      {/* Card context menu (right-click on desktop / long-press on touch) */}
      {ctxMenu && (
        <div
          data-testid="jfb-ctx-menu"
          style={{
            position: "fixed", left: ctxMenu.x, top: ctxMenu.y, zIndex: 250,
            background: "#fff", border: "1px solid var(--line)", borderRadius: 10,
            boxShadow: "0 18px 38px rgba(0,0,0,.18)", minWidth: 220,
            padding: 6, fontSize: 13,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CtxItem icon={Eye} label="Open full detail" onClick={() => { setOpenJobId(ctxMenu.card.job_id); setCtxMenu(null); }} testid="jfb-ctx-open" />
          <div style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />
          <CtxItem icon={Wrench} label="Mark In Dispatch" onClick={() => { setSpecialModal({ kind: "dispatch_ready", card: ctxMenu.card }); setCtxMenu(null); }} testid="jfb-ctx-dispatch" />
          <CtxItem icon={SubIcon} label="Send to Subcontractor" onClick={() => { setSpecialModal({ kind: "subcon", card: ctxMenu.card }); setCtxMenu(null); }} testid="jfb-ctx-subcon" />
          <CtxItem icon={MessageSquare} label="Request Customer Input" onClick={() => { setSpecialModal({ kind: "customer_issue", card: ctxMenu.card }); setCtxMenu(null); }} testid="jfb-ctx-customer" />
          <CtxItem icon={DollarSign} label="Capture Invoice / Awaiting Payment" onClick={() => { setSpecialModal({ kind: "invoice", card: ctxMenu.card }); setCtxMenu(null); }} testid="jfb-ctx-invoice" />
        </div>
      )}
    </>
  );
}

function CtxItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "8px 10px", background: "transparent", border: "none",
        borderRadius: 6, cursor: "pointer", textAlign: "left",
        fontSize: 13, color: "var(--text)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--card-2)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}
